import { memo, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import type { FeaturedCategory } from '@apiTypes';
import {
    Button,
    ChevronLeftIcon,
    ChevronRightIcon,
    Heading,
    Image,
} from '@library';
import withErrorBoundary from '../../../../core/components/errors/withErrorBoundary';
import useCurrentStore from '../../../../core/hooks/useCurrentStore';
import CustomLink from '../../../../core/components/CustomLink';
import Carousel from '../../../../core/components/Carousel';
import useCarouselActions from '../../../../core/hooks/useCarouselActions';
import useProductListRootCategories from '../../../queries/useProductListRootCategories';
import type Category from '../../../../core/types/category';
import useProductListQueryFilters from '../../../hooks/useProductListQueryFilters';
import useCategoryTree from '../../../queries/useCategoryTree';
import styles from './ProductListCategoryTiles.module.scss';

const matchesCategory = (
    category: FeaturedCategory,
    availableCategory: Category
): boolean => {
    if (availableCategory.id === String(category.id)) {
        return true;
    }
    if (availableCategory.children.length > 0) {
        return availableCategory.children.some(child =>
            matchesCategory(category, child)
        );
    }
    return false;
};

const filterFeaturedCategories = (
    featuredCategories: FeaturedCategory[],
    availableCategories: Category[]
) => {
    return featuredCategories.filter(category =>
        availableCategories.some(availableCategory =>
            matchesCategory(category, availableCategory)
        )
    );
};

const ProductListCategoryTiles = () => {
    const { categoryId } = useParams();

    const {
        preorderSeasonId,
        includeExcludedCategories,
        language,
        store,
        vehicleId,
        rootCategoryId,
        skip,
    } = useProductListQueryFilters();

    const {
        features: { featuredCategories },
    } = useCurrentStore();

    const { categories } = useProductListRootCategories({
        variables: {
            language,
            store,
            filters: [],
            states: [],
            vehicleId,
            includeExcludedCategories,
            preorderSeasonId,
        },
        skip,
        rootCategoryId,
        categoryId,
        rootTree: true,
    });

    const { previousItem, nextItem, controlsState, handleChangeActions } =
        useCarouselActions({ watchControlState: true });

    const { tree } = useCategoryTree({ categories });

    const filteredCategories = useMemo(() => {
        if (!featuredCategories?.categories || !featuredCategories.enabled)
            return [];

        return filterFeaturedCategories(featuredCategories.categories, tree);
    }, [tree, featuredCategories]);

    if (!featuredCategories?.enabled || !vehicleId) {
        return null;
    }

    const searchParams = new URLSearchParams({
        vehicleId: vehicleId,
    }).toString();

    return (
        <div className={styles.categoryTilesWrapper}>
            {controlsState.showLeft && (
                <div className={styles.previousItemButton}>
                    <Button
                        text
                        tertiary
                        theme={styles.theme}
                        iconButton
                        onClick={previousItem}
                    >
                        <ChevronLeftIcon
                            size={24}
                            iconLabel="tile-previous-icon"
                        />
                    </Button>
                </div>
            )}
            <Carousel observeCarouselSize onChangeActions={handleChangeActions}>
                {filteredCategories.map(category => (
                    <CustomLink
                        key={category.id}
                        to={`../categories/${category.id}?${searchParams}`}
                        role="button"
                        tabIndex={0}
                        className={styles.categoryTileLink}
                        title={category.displayName[language]}
                        data-test-id={category.id}
                    >
                        <div
                            className={clsx(styles.categoryTile, {
                                [styles.categoryTileActive]:
                                    categoryId === String(category.id),
                            })}
                        >
                            <Image
                                src={category.iconUrl}
                                alt={category.displayName.en}
                                size="32px"
                            />
                            <Heading>
                                <span className={styles.categoryTileText}>
                                    {category.displayName[language]}
                                </span>
                            </Heading>
                        </div>
                    </CustomLink>
                ))}
            </Carousel>
            {controlsState.showRight && (
                <div className={styles.nextItemButton}>
                    <Button
                        text
                        tertiary
                        theme={styles.theme}
                        iconButton
                        onClick={nextItem}
                    >
                        <ChevronRightIcon iconLabel="tile-next-icon" />
                    </Button>
                </div>
            )}
        </div>
    );
};

export default memo(withErrorBoundary(ProductListCategoryTiles));
